import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { facilitiesAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Star,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Ambulance,
  Stethoscope,
  CheckCircle,
  User,
  Calendar,
  MessageSquare,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const FACILITY_LABELS = {
  hospital: 'Hôpital',
  clinic: 'Clinique',
  pharmacy: 'Pharmacie',
  laboratory: 'Laboratoire',
  imaging_center: "Centre d'imagerie",
  maternity: 'Maternité',
  dental_clinic: 'Cabinet dentaire'
};

const FacilityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [facility, setFacility] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Review modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadFacility();
  }, [id]);

  const loadFacility = async () => {
    try {
      const response = await facilitiesAPI.getOne(id);
      setFacility(response.data.facility);
      setReviews(response.data.reviews || []);
      setProfessionals(response.data.professionals || []);
    } catch (error) {
      console.error('Error loading facility:', error);
      toast.error('Établissement non trouvé');
      navigate('/facilities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) {
      toast.error('Veuillez donner une note');
      return;
    }

    setSubmitting(true);
    try {
      await facilitiesAPI.addReview(id, {
        rating: reviewRating,
        comment: reviewComment.trim() || null
      });
      
      toast.success('Avis ajouté avec succès');
      setShowReviewModal(false);
      setReviewRating(0);
      setReviewComment('');
      loadFacility();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de l\'avis');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
      </div>
    );
  }

  if (!facility) return null;

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="facility-detail-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/facilities')}
        className="mb-4 text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour aux établissements
      </Button>

      {/* Header Card */}
      <Card className="border-0 shadow-sm mb-6 overflow-hidden">
        {facility.image_url && (
          <div className="h-48 md:h-64 bg-slate-100">
            <img 
              src={facility.image_url} 
              alt={facility.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-blue-100 text-blue-700">
                  {FACILITY_LABELS[facility.facility_type]}
                </Badge>
                {facility.is_verified && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Vérifié
                  </Badge>
                )}
                {facility.emergency_24h && (
                  <Badge className="bg-red-100 text-red-700">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Urgences 24h
                  </Badge>
                )}
                {facility.has_ambulance && (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Ambulance className="w-3 h-3 mr-1" />
                    Ambulance
                  </Badge>
                )}
              </div>
              
              <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {facility.name}
              </h1>
              
              {/* Rating */}
              <div className="flex items-center gap-2 mt-2">
                {facility.avg_rating ? (
                  <>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${star <= facility.avg_rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                        />
                      ))}
                    </div>
                    <span className="font-semibold text-slate-700">{facility.avg_rating}</span>
                    <span className="text-slate-400">({facility.review_count} avis)</span>
                  </>
                ) : (
                  <span className="text-slate-400">Pas encore d'avis</span>
                )}
              </div>
            </div>

            {isAuthenticated && user?.role === 'patient' && (
              <Button
                onClick={() => setShowReviewModal(true)}
                className="bg-[#1976D2] hover:bg-[#1565C0]"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Laisser un avis
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {facility.description && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">À propos</h2>
                <p className="text-slate-600 whitespace-pre-wrap">{facility.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Services */}
          {facility.services && facility.services.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">Services</h2>
                <div className="flex flex-wrap gap-2">
                  {facility.services.map((service, index) => (
                    <Badge key={index} variant="outline" className="bg-slate-50">
                      {service}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Professionals */}
          {professionals.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Professionnels</h2>
                <div className="space-y-3">
                  {professionals.map((pro) => (
                    <div 
                      key={pro.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/professional/${pro.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1976D2] flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Dr. {pro.full_name}</p>
                          <p className="text-sm text-slate-500">{pro.specialty}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        <Calendar className="w-4 h-4 mr-1" />
                        RDV
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Avis ({reviews.length})
              </h2>
              
              {reviews.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Aucun avis pour le moment</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-slate-100 pb-4 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <span className="font-medium text-slate-700">{review.user_name}</span>
                        </div>
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-slate-600 text-sm">{review.comment}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(review.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Contact</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#E53935] mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-700">Adresse</p>
                    <p className="text-slate-500">{facility.address}</p>
                    <p className="text-slate-500">{facility.city}</p>
                  </div>
                </div>
                
                {facility.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-[#1976D2] mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Téléphone</p>
                      <a href={`tel:${facility.phone}`} className="text-[#1976D2] hover:underline">
                        {facility.phone}
                      </a>
                    </div>
                  </div>
                )}

                {facility.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[#1976D2] mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Email</p>
                      <a href={`mailto:${facility.email}`} className="text-[#1976D2] hover:underline">
                        {facility.email}
                      </a>
                    </div>
                  </div>
                )}

                {facility.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-[#1976D2] mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Site web</p>
                      <a href={facility.website} target="_blank" rel="noopener noreferrer" className="text-[#1976D2] hover:underline">
                        {facility.website}
                      </a>
                    </div>
                  </div>
                )}

                {facility.opening_hours && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Horaires</p>
                      <p className="text-slate-500 whitespace-pre-wrap">{facility.opening_hours}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Map Placeholder */}
          {facility.latitude && facility.longitude && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0 overflow-hidden rounded-xl">
                <div className="h-48 bg-slate-100 flex items-center justify-center">
                  <div className="text-center text-slate-400">
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Carte disponible bientôt</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Laisser un avis</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Rating */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Votre note</p>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewRating(star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= reviewRating 
                          ? 'text-amber-400 fill-amber-400' 
                          : 'text-slate-200 hover:text-amber-200'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Votre commentaire (optionnel)</p>
              <Textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Partagez votre expérience..."
                rows={4}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReviewModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={submitting || reviewRating === 0}
                className="flex-1 bg-[#1976D2] hover:bg-[#1565C0]"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityDetail;
